class DropShippingAddresses extends AppBase {
  constructor() {
    super();

    this.wrapper = document.querySelector('[data-customer-addresses]');
    this.companyId = this.wrapper?.dataset?.companyId;
    this.tableBody = document.getElementById('DropShippingAddressTableBody');
    this.modalWrapper = document.getElementById('DropShippingAddressModals');
    this.addresses = [];
    this.dataTable = null;

    if (this.companyId && this.tableBody) {
      this.init();
    }
  }

  async init() {
    await this.loadAddresses();
    this.addEventListeners();
  }

  async loadAddresses() {
    this.toggleLoadingOverlay('show');

    try {
      const response = await this.fetchData(
        `${window.appApiUrl}get-drop-shipping-addresses?companyId=${encodeURIComponent(this.companyId)}`
      );

      if (!response || !response.data) {
        throw new Error('Unable to fetch drop shipping addresses');
      }

      this.addresses = Array.isArray(response.data) ? response.data : [];
      window.currentCustomer = window.currentCustomer || {};
      window.currentCustomer.shippingAddresses = this.addresses;

      this.renderAddressRows();
      this.renderAddressModals();
      this.updateDefaultAddressStyle();

      setTimeout(() => {
        this.initializeAddressEnhancements();
      }, 100);

      this.initializeDataTable();
      this.autoSelectDefaultAddress();

      this.toggleLoadingOverlay('hide');
    } catch (error) {
      console.error('Error loading addresses:', error);

      this.tableBody.innerHTML = `
        <tr>
          <td colspan="8" class="text-center">Unable to load addresses. Please try again.</td>
        </tr>
      `;

      this.toggleLoadingOverlay('hide');
    }
  }

  renderAddressRows() {
    const activeAddresses = this.addresses.filter(
      (address) => !address.deletedAt && !address.isDeleted
    );

    if (!activeAddresses.length) {
      this.tableBody.innerHTML = `
        <tr>
          <td colspan="8" class="text-center">No addresses found.</td>
        </tr>
      `;
      return;
    }

    this.tableBody.innerHTML = activeAddresses
      .map((address) => this.createAddressRow(address))
      .join('');
  }

  createAddressRow(address) {
    const addressId = this.escapeHtml(address.addressId);
    const isFavorite = Boolean(address.isFavorite);
    const typeLabel = address.type === 'drop-ship' ? 'Drop-ship' : 'Store';
    const phoneExtension = address.phoneExtension
      ? ` x${this.escapeHtml(address.phoneExtension)}`
      : '';

    return `
      <tr class="address-row" data-address-id="${addressId}">
        <td width="10%" class="no-select">
          <a
            href="javascript:void(0);"
            class="mark-favorite-address icon-star${isFavorite ? ' icon-filled' : ''}"
            data-shipping-address-id="${addressId}"
          >
            <span class="visually-hidden">${isFavorite ? '1' : '0'}</span>
          </a>
        </td>

        <td width="12%">
          ${this.formatDate(address.createdAt)}
        </td>

        <td width="8%">
          ${typeLabel}
        </td>

        <td width="11%">
          ${this.escapeHtml(address.company || '')}
          <sup class="table-default-address" data-address-id="${addressId}"></sup>
        </td>

        <td width="11%">
          ${this.escapeHtml(address.contactName || '')}
        </td>

        <td width="21%">
          <div class="address">
            <span class="cus-addr">${this.escapeHtml(address.address1 || '')}</span>
            ${address.address2 ? `<span class="cus-addr">${this.escapeHtml(address.address2)}</span>` : ''}
            <span class="cus-addr">
              ${this.escapeHtml(address.city || '')}
              ${this.escapeHtml((address.province || '').toUpperCase())}
              ${this.escapeHtml(address.zip || '')}
              ${this.escapeHtml(address.country || '')}
            </span>
          </div>
        </td>

        <td width="13%">
          ${this.escapeHtml(address.phone || '')}${phoneExtension}
        </td>
      </tr>
    `;
  }

  renderAddressModals() {
    const activeAddresses = this.addresses.filter(
      (address) => !address.deletedAt && !address.isDeleted
    );

    this.modalWrapper.innerHTML = activeAddresses
      .map((address) => this.createAddressModal(address))
      .join('');
  }

  createAddressModal(address) {
    const addressId = this.escapeHtml(address.addressId);
    const formId = `EditForm_${addressId}`;

    return `
      <div id="EditAddress_${addressId}" class="address-form modal" style="display: none;">
        <div class="modal-content">
          <div class="modal-header">
            <h2>Edit Address</h2>
            <button
              class="close"
              type="button"
              data-close-modal="${addressId}"
            >
              &times;
            </button>
          </div>

          <div class="modal-body">
            <div class="alert-messages alert">
              <div class="alert-box">
                <div class="alert-icon alert-icon-warning">
                  <svg aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 554.2 554.199" class="icon icon-warning"><g><path d="M538.5,386.199L356.5,70.8c-16.4-28.4-46.7-45.9-79.501-45.9c-32.8,0-63.1,17.5-79.5,45.9L12.3,391.6 c-16.4,28.4-16.4,63.4,0,91.8C28.7,511.8,59,529.3,91.8,529.3H462.2c0.101,0,0.2,0,0.2,0c50.7,0,91.8-41.101,91.8-91.8 C554.2,418.5,548.4,400.8,538.5,386.199z M316.3,416.899c0,21.7-16.7,38.3-39.2,38.3s-39.2-16.6-39.2-38.3V416 c0-21.601,16.7-38.301,39.2-38.301S316.3,394.3,316.3,416V416.899z M317.2,158.7L297.8,328.1c-1.3,12.2-9.4,19.8-20.7,19.8 s-19.4-7.7-20.7-19.8L237,158.6c-1.3-13.1,5.801-23,18-23H299.1C311.3,135.7,318.5,145.6,317.2,158.7z"/></g></svg>
                </div>
                <div class="alert-icon alert-icon-info">
                  <svg height="48" viewBox="0 0 48 48" width="48" xmlns="http://www.w3.org/2000/svg"><path d="M22 34h4V22h-4v12zm2-30C12.95 4 4 12.95 4 24s8.95 20 20 20 20-8.95 20-20S35.05 4 24 4zm0 36c-8.82 0-16-7.18-16-16S15.18 8 24 8s16 7.18 16 16-7.18 16-16 16zm-2-22h4v-4h-4v4z"/></svg>
                </div>
                <div class="alert-icon alert-icon-success">
                  <svg viewBox="0 0 512 512" class="icon icon-check" id="icon-check"><path d="M435.848 83.466L172.804 346.51l-96.652-96.652c-4.686-4.686-12.284-4.686-16.971 0l-28.284 28.284c-4.686 4.686-4.686 12.284 0 16.971l133.421 133.421c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-28.284-28.284c-4.686-4.686-12.284-4.686-16.97 0z" class=""></path></svg>
                </div>
                <div class="alert-text"></div>
              </div>
            </div>

            <form id="${formId}" class="edit-address-form" data-address-id="${addressId}">
              <input type="hidden" name="companyId" value="${this.escapeHtml(this.companyId)}">
              <input type="hidden" name="addressId" value="${addressId}">
              <input type="hidden" name="isFavorite" value="${this.escapeHtml(address.isFavorite || false)}">

              ${this.getAddressFormFields(address, formId)}

              <div class="form-actions">
                <button type="submit" class="button button--primary">
                  Update
                </button>
                <button
                  class="button button--secondary"
                  type="button"
                  data-close-modal="${addressId}"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    `;
  }

  getAddressFormFields(address, formId) {
    const addressType = address.type || '';
    const country = address.country || 'United States';

    return `
      <div class="address-form--wrapper">
        <div class="form-field">
          <div class="form__select select">
            <select
              class="select__select"
              id="AddressType_${formId}"
              name="type"
              autocomplete="type"
              required="true"
            >
              <option value="" ${addressType === '' ? 'selected' : ''}></option>
              <option value="store" ${addressType === 'store' ? 'selected' : ''}>Store</option>
              <option value="drop-ship" ${addressType === 'drop-ship' ? 'selected' : ''}>Drop-ship</option>
            </select>
            <label class="form-label" for="AddressType_${formId}">Type</label>
          </div>
        </div>

        ${this.getTextInput('contactName', 'AddressContactName', formId, address.contactName, 'Contact Name', true)}
        ${this.getTextInput('address1', 'AddressAddress1', formId, address.address1, 'Address 1', true)}
        ${this.getTextInput('address2', 'AddressAddress2', formId, address.address2, 'Address 2 (Optional)', false)}

        <div class="form-field-group">
          ${this.getTextInput('city', 'AddressCity', formId, address.city, 'City', true)}
          ${this.getTextInput('zip', 'AddressZip', formId, address.zip, 'Zip', true)}
        </div>

        <div class="form-field">
          <div class="form__select select">
            <select
              class="select__select"
              id="AddressCountry_${formId}"
              name="country"
              data-address-country-select
              data-default="${this.escapeHtml(country)}"
              data-form-id="${formId}"
              autocomplete="country"
              required="true"
            >
              ${window.shopifyCountryOptions || ''}
            </select>
            <label class="form-label" for="AddressCountry_${formId}">Country</label>
          </div>
        </div>

        <div class="form-field" id="AddressProvinceContainer_${formId}" style="display:none;">
          <div class="form__select select">
            <select
              class="select__select"
              id="AddressProvince_${formId}"
              name="province"
              data-default="${this.escapeHtml(address.province || '')}"
              autocomplete="address-level1"
              required="true"
            ></select>
            <label class="form-label" for="AddressProvince_${formId}">Province</label>
          </div>
        </div>

        ${this.getTextInput('phone', 'AddressPhone', formId, address.phone, 'Phone', true, 'tel')}
        ${this.getTextInput('phoneExtension', 'AddressPhoneExtension', formId, address.phoneExtension, 'Phone Extension (Optional)', false, 'tel')}
        ${this.getTextInput('company', 'AddressCompany', formId, address.company, 'Company Name (Optional)', false)}

        <div class="form-field form-field--full d-flex">
          <input
            type="checkbox"
            name="isDefault"
            id="AddressIsDefault_${formId}"
            ${address.isDefault ? 'checked' : ''}
          >
          <label class="form-label form-label--checkbox" for="AddressIsDefault_${formId}">
            Set as default address
          </label>
        </div>
      </div>
    `;
  }

  getTextInput(name, idPrefix, formId, value, label, required = false, type = 'text') {
    const attrs = type === 'tel' && name === 'phoneExtension'
      ? 'pattern="[0-9]*" maxlength="5"'
      : '';

    return `
      <div class="form-field">
        <input
          class="form-input form-input-placeholder"
          type="${type}"
          id="${idPrefix}_${formId}"
          name="${name}"
          value="${this.escapeHtml(value || '')}"
          placeholder="${label}"
          ${required ? 'required="true"' : ''}
          ${attrs}
        >
        <label class="form-label" for="${idPrefix}_${formId}">
          ${label}
        </label>
      </div>
    `;
  }

  addEventListeners() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('.address-row:not(.no-select)')) {
        const row = e.target.closest('.address-row');
        if (!e.target.closest('.no-select')) {
          const addressId = row.dataset.addressId;
          this.selectAddress(addressId);
        }
      }

      if (e.target.closest('.select-address-btn')) {
        e.preventDefault();
        const btn = e.target.closest('.select-address-btn');
        const addressId = btn.dataset.shippingAddressId;
        this.selectAddress(addressId);
      }

      if (e.target.closest('.edit-address-form-button')) {
        e.preventDefault();
        const btn = e.target.closest('.edit-address-form-button');
        const addressId = btn.dataset.addressId;
        this.openModal(addressId);
      }

      if (e.target.closest('.remove-address-button')) {
        e.preventDefault();
        const btn = e.target.closest('.remove-address-button');
        const addressId = btn.dataset.shippingAddressId;
        this.deleteAddress(addressId);
      }

      if (e.target.closest('.mark-favorite-address')) {
        e.preventDefault();
        const link = e.target.closest('.mark-favorite-address');
        const addressId = link.dataset.shippingAddressId;
        this.toggleFavorite(addressId);
      }

      if (e.target.closest('[data-close-modal]')) {
        e.preventDefault();
        const btn = e.target.closest('[data-close-modal]');
        const addressId = btn.dataset.closeModal;
        this.closeModal(addressId);
      }

      if (e.target.closest('#EditFormButton_New')) {
        e.preventDefault();
        this.openModal('New');
      }
    });

    document.addEventListener('submit', (e) => {
      if (e.target.closest('.add-address-form')) {
        e.preventDefault();
        this.handleAddAddress(e.target);
      }

      if (e.target.closest('.edit-address-form')) {
        e.preventDefault();
        this.handleUpdateAddress(e.target);
      }
    });

    document.addEventListener('input', (e) => {
      if (e.target.name === 'phone') {
        this.validatePhoneNumber(e.target);
      }
    });
  }

  async selectAddress(addressId) {
    const address = this.addresses.find((a) => String(a.addressId) === String(addressId));

    if (!address) return;

    const addressPlaceholder = document.querySelector('.address-placeholder');

    if (addressPlaceholder) {
      // Populate all data attributes
      addressPlaceholder.setAttribute('data-first-name', address.contactName || '');
      addressPlaceholder.setAttribute('data-contact-name', address.contactName || '');
      addressPlaceholder.setAttribute('data-company', address.company || '');
      addressPlaceholder.setAttribute('data-address-line1', address.address1 || '');
      addressPlaceholder.setAttribute('data-address-line2', address.address2 || '');
      addressPlaceholder.setAttribute('data-address-city', address.city || '');
      addressPlaceholder.setAttribute('data-address-country', address.countryCode || '');
      addressPlaceholder.setAttribute('data-address-province', address.provinceCode || '');
      addressPlaceholder.setAttribute('data-address-zip', address.zip || '');
      addressPlaceholder.setAttribute('data-phone', address.phone || '');
      addressPlaceholder.setAttribute('data-phone-extension', address.phoneExtension || '');
      addressPlaceholder.setAttribute('data-address-id', address.addressId);

      // Build address HTML
      let addressHTML = `
        <span class="cus-addr">${address.address1}</span>
        <span class="cus-addr">${address.address2 || ''}</span>
        <span class="cus-addr">${address.city || ''} ${address.province?.toUpperCase() || ''} ${address.zip || ''} ${address.country}</span>
      `;

      let phone = address?.phone || '';
      if (phone && address?.phoneExtension) {
        phone += ' x' + address.phoneExtension;
        addressHTML += `<span class="cus-addr">${phone}</span>`;
      }

      addressPlaceholder.innerHTML = `<span class="cus-addr">${address?.contactName || ''}</span>` + addressHTML;
      addressPlaceholder.classList.remove('error');
      addressPlaceholder.classList.add('selected');

      document.querySelector('.change-shipping-address')?.classList.remove('hide');
      document.querySelector('.close-address-modal')?.click();
    }

    localStorage.setItem('selectedShippingAddressId', addressId);

    this.alertMessages(
      '#orders .alert-messages',
      'success',
      'Ship To address updated.'
    );

    setTimeout(() => {
      document.querySelector('#orders .alert-messages')?.classList.add('fade-out');

      setTimeout(() => {
        document.querySelector('#orders .alert-messages')?.classList.remove('alert-show');
        document.querySelector('#orders .alert-messages')?.classList.remove('fade-out');
      }, 600);
    }, 3000);
  }

  async toggleFavorite(addressId) {
    const link = document.querySelector(`.mark-favorite-address[data-shipping-address-id="${addressId}"]`);
    const formElement = document.querySelector(`#EditForm_${addressId}`);

    if (!formElement) return;

    const isFavoriteInput = formElement.querySelector('[name="isFavorite"]');

    if (isFavoriteInput) {
      isFavoriteInput.value = link.classList.contains('icon-filled') ? "false" : "true";

      // Temporarily disable validation, submit, then re-enable
      const originalNoValidate = formElement.noValidate;
      formElement.noValidate = true;

      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      formElement.dispatchEvent(submitEvent);

      formElement.noValidate = originalNoValidate;
    }
  }

  async handleAddAddress(form) {
    if (form.querySelector('.error-field')) {
      return false;
    }

    const formData = new FormData(form);
    const companyId = formData.get('companyId');
    const isDefaultAddress = !!formData.get('isDefault');

    if (!window.ShopifyCountries || window.ShopifyCountries.length === 0) {
      return false;
    }

    this.toggleLoadingOverlay('show');

    const country = window.ShopifyCountries.find((c) => c.name.trim() === formData.get('country').trim());
    let provinceCode = country?.zones.find((z) => z.name === formData.get('province'))?.code || formData.get('province');
    const addressIdentifier = Math.random().toString(36).slice(2, 7);

    let requestData = {
      'companyId': companyId,
      'shippingAddress': {
        'identifier': addressIdentifier,
        'company': formData.get('company'),
        'contactName': formData.get('contactName'),
        'address1': formData.get('address1'),
        'address2': formData.get('address2'),
        'city': formData.get('city'),
        'zip': formData.get('zip'),
        'country': formData.get('country'),
        'countryCode': country.code,
        'province': formData.get('province'),
        'provinceCode': provinceCode,
        'phone': formData.get('phone'),
        'phoneExtension': formData.get('phoneExtension') || '',
        'isFavorite': formData.get('isFavorite') === 'true' ? true : false,
        'type': formData.get('type'),
        'isDefault': isDefaultAddress
      }
    };

    try {
      const response = await this.fetchData(
        `${window.appApiUrl}upsert-drop-shipping-address`,
        {
          method: 'POST',
          body: JSON.stringify(requestData)
        }
      );

      if (response) {
        const addressMetafield = response.data.find(metafield => metafield.namespace === 'global' && metafield.key === 'drop_shipping_addresses');
        const addresses = addressMetafield?.value ? JSON.parse(addressMetafield.value) : null;

        if (addresses) {
          window.currentCustomer = window.currentCustomer || {};
          window.currentCustomer.shippingAddresses = addresses;
          this.addresses = addresses;

          const newAddress = addresses.find(address => address.identifier === addressIdentifier);

          if (newAddress && this.dataTable) {
            this.dataTable.rows.add(this.formatAddressRowData(newAddress, companyId));
            this.renderAddressModals();
            this.updateDefaultAddressStyle();

            setTimeout(() => {
              this.initializeAddressEnhancements();
            }, 100);
          }

          this.closeModal('New');

          setTimeout(() => {
            form.reset();
          }, 100);

          this.alertMessages(
            '[data-customer-addresses] .alert-messages',
            'success',
            '<strong>Success!</strong><br />Address has been added successfully.',
            true
          );
        }
      }
    } catch (error) {
      console.error('Error adding address:', error);
      this.alertMessages(
        '#EditAddress_New .alert-messages',
        'error',
        'Failed to add address. Please try again.',
        false
      );
    }

    this.toggleLoadingOverlay('hide');
  }

  async handleUpdateAddress(form) {
    if (form.querySelector('.error-field')) {
      return false;
    }

    const formData = new FormData(form);
    const addressId = parseInt(formData.get('addressId')) || '';
    const companyId = formData.get('companyId');
    const isDefaultAddress = !!formData.get('isDefault');

    if (!window.ShopifyCountries || window.ShopifyCountries.length === 0) {
      return false;
    }

    this.toggleLoadingOverlay('show');

    const country = window.ShopifyCountries.find((c) => c.name.trim() === formData.get('country').trim());
    let provinceCode = country?.zones.find((z) => z.name === formData.get('province'))?.code || formData.get('province');

    let requestData = {
      'companyId': companyId,
      'shippingAddress': {
        'addressId': addressId,
        'company': formData.get('company'),
        'contactName': formData.get('contactName'),
        'address1': formData.get('address1'),
        'address2': formData.get('address2'),
        'city': formData.get('city'),
        'zip': formData.get('zip'),
        'country': formData.get('country'),
        'countryCode': country.code,
        'province': formData.get('province'),
        'provinceCode': provinceCode,
        'phone': formData.get('phone'),
        'phoneExtension': formData.get('phoneExtension') || '',
        'isFavorite': formData.get('isFavorite') === 'true' ? true : false,
        'type': formData.get('type'),
        'isDefault': isDefaultAddress
      }
    };

    try {
      const response = await this.fetchData(
        `${window.appApiUrl}upsert-drop-shipping-address`,
        {
          method: 'POST',
          body: JSON.stringify(requestData)
        }
      );

      if (response) {
        const addressMetafield = response.data.find(metafield => metafield.namespace === 'global' && metafield.key === 'drop_shipping_addresses');
        const addresses = addressMetafield?.value ? JSON.parse(addressMetafield.value) : null;

        if (addresses) {
          window.currentCustomer = window.currentCustomer || {};
          window.currentCustomer.shippingAddresses = addresses;
          this.addresses = addresses;

          const updatedAddress = addresses.find(address => parseInt(address.addressId) === addressId);

          if (updatedAddress && this.dataTable) {
            const updatedElement = document.querySelector(`button[data-address-id="${addressId}"]`)?.closest('tr');
            if (updatedElement) {
              const rowIndex = parseInt(updatedElement.getAttribute('data-index'));
              this.dataTable.rows.updateRow(rowIndex, this.formatAddressRowData(updatedAddress, companyId));
            }
            this.renderAddressModals();
            this.updateDefaultAddressStyle();

            setTimeout(() => {
              this.initializeAddressEnhancements();
            }, 100);
          }

          this.closeModal(addressId);

          this.alertMessages(
            '[data-customer-addresses] .alert-messages',
            'success',
            '<strong>Success!</strong><br />Address has been updated successfully.',
            true
          );
        }
      }
    } catch (error) {
      console.error('Error updating address:', error);
      this.alertMessages(
        `#EditAddress_${addressId} .alert-messages`,
        'error',
        'Failed to update address. Please try again.',
        false
      );
    }

    this.toggleLoadingOverlay('hide');
  }

  async deleteAddress(addressId) {
    if (!confirm('Are you sure you want to delete this address?')) {
      return;
    }

    const deletedElement = document.querySelector(`button[data-address-id="${addressId}"]`)?.closest('tr');
    const rowIndex = deletedElement ? parseInt(deletedElement.getAttribute('data-index')) : null;

    this.toggleLoadingOverlay('show');

    try {
      const response = await this.fetchData(
        `${window.appApiUrl}delete-drop-shipping-address`,
        {
          method: 'POST',
          body: JSON.stringify({
            companyId: this.companyId,
            addressId: addressId
          })
        }
      );

      if (response) {
        const addressMetafield = response.data.find(metafield => metafield.namespace === 'global' && metafield.key === 'drop_shipping_addresses');
        const addresses = addressMetafield?.value ? JSON.parse(addressMetafield.value) : null;

        if (addresses) {
          window.currentCustomer = window.currentCustomer || {};
          window.currentCustomer.shippingAddresses = addresses;
          this.addresses = addresses;

          if (this.dataTable && rowIndex !== null) {
            const searchValue = document.querySelector('.datatable-search .datatable-input')?.value || '';

            this.dataTable.refresh();
            this.dataTable.rows.remove(rowIndex);

            const searchInput = document.querySelector('.datatable-search .datatable-input');
            if (searchInput) {
              searchInput.value = searchValue;
              searchInput.dispatchEvent(new Event('input', { bubbles: true }));
            }

            const typeSelect = document.querySelector('.datatable-type-select');
            if (typeSelect) {
              typeSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }

          this.renderAddressModals();
          this.updateDefaultAddressStyle();
        }

        this.alertMessages(
          '[data-customer-addresses] .alert-messages',
          'success',
          '<strong>Success!</strong><br />Address has been deleted successfully.',
          true
        );
      }
    } catch (error) {
      console.error('Error deleting address:', error);
      this.alertMessages(
        '[data-customer-addresses] .alert-messages',
        'error',
        'Failed to delete address. Please try again.',
        true
      );
    }

    this.toggleLoadingOverlay('hide');
  }

  openModal(addressId) {
    document.querySelectorAll('.address-form').forEach((modal) => {
      modal.style.display = 'none';
    });

    const modal = document.getElementById(`EditAddress_${addressId}`);

    if (modal) {
      modal.style.display = 'block';
    }
  }

  closeModal(addressId) {
    const modal = document.getElementById(`EditAddress_${addressId}`);

    if (modal) {
      modal.style.display = 'none';

      const form = modal.querySelector('form');
      if (form) {
        form.reset();
      }
    }
  }

  initializeAddressEnhancements() {
    if (!window.Shopify?.CountryProvinceSelector) {
      return;
    }

    document.querySelectorAll('.address-form form').forEach((formElement) => {
      if (!formElement || !formElement.id) return;

      const formId = formElement.id;
      const countryId = `AddressCountry_${formId}`;
      const provinceId = `AddressProvince_${formId}`;
      const provinceContainerId = `AddressProvinceContainer_${formId}`;

      const countryEl = document.getElementById(countryId);
      const provinceEl = document.getElementById(provinceId);
      const provinceContainerEl = document.getElementById(provinceContainerId);

      if (countryEl && provinceEl && provinceContainerEl && countryEl.options && countryEl.options.length > 0) {
        try {
          new Shopify.CountryProvinceSelector(countryId, provinceId, {
            hideElement: provinceContainerId
          });
        } catch (error) {
          console.warn(`Failed to initialize CountryProvinceSelector for ${formId}:`, error);
        }
      }
    });

    if (window.google?.maps?.places) {
      this.initAutocomplete();
    }
  }

  initAutocomplete() {
    document.querySelectorAll('.address-form').forEach((form) => {
      this.initGoogleMapEvent(form);
    });
  }

  initGoogleMapEvent(form) {
    const address1 = form.querySelector('input[name="address1"]');
    const city = form.querySelector('input[name="city"]');
    const province = form.querySelector('select[name="province"]');
    const zip = form.querySelector('input[name="zip"]');
    const country = form.querySelector('select[name="country"]');

    if (!address1 || !city || !province || !zip || !country || !window.google?.maps?.places) {
      return;
    }

    const autocomplete = new google.maps.places.Autocomplete(address1, {
      types: ['geocode', 'establishment']
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      const addressComponents = place?.address_components || [];
      const address = {};

      addressComponents.forEach((item) => {
        address[item.types[0]] = item.long_name;
      });

      address1.value = `${address.street_number || ''} ${address.route || ''}`.trim();
      city.value = address.postal_town || address.locality || '';
      country.value = address.country || 'United States';
      country.dispatchEvent(new Event('change'));
      province.value = address.administrative_area_level_1 || '';
      province.dispatchEvent(new Event('change'));
      zip.value = address.postal_code || address.postal_code_prefix || '';
    });
  }

  validatePhoneNumber(input) {
    let raw = input.value.replace(/[^\d]/g, '');
    const form = input.closest('.address-form');
    const countrySelect = form?.querySelector('select[name="country"]');
    const countryCode = countrySelect?.value || 'United States';

    if (countryCode === 'United States' || countryCode === 'Canada') {
      const area = raw.slice(0, 3);
      const prefix = raw.slice(3, 6);
      const line = raw.slice(6, 10);

      let formatted = '';
      if (area) formatted += area;
      if (prefix) formatted += '-' + prefix;
      if (line) formatted += '-' + line;

      input.value = formatted;
    } else {
      input.value = '+' + raw.slice(0, 15);
    }
  }

  initializeDataTable() {
    const table = document.querySelector('.table-addresses');

    if (!table || !window.simpleDatatables?.DataTable) return;

    if (this.dataTable) {
      this.dataTable.destroy();
    }

    try {
      this.dataTable = new simpleDatatables.DataTable(table, {
        searchable: true,
        sortable: true,
        perPage: 10,
        perPageSelect: [10, 25, 50, 100],
        pagerDelta: 2,
        labels: {
          placeholder: 'Search addresses...',
          perPage: 'Results per page',
          noRows: 'No addresses found',
          info: '{start}-{end} of {rows} Records'
        },
        columns: [
          { select: 0, sortable: false, searchable: false },
          {
            select: 1,
            type: 'date',
            format: 'MM/DD/YYYY',
            render: function (value, td) {
              return `<span class="datatable-date"><span>${value}</span></span>`;
            }
          },
          { select: 7, sortable: false, searchable: false }
        ],
        template: (options, dom) => `
          <div class="${options.classes.top}">
            <div class="datable-select-field">
              <select name="datatable-type-input" data-placeholder="Filter By" class="datatable-type-select">
                <option value="" disabled selected>Filter By</option>
                <option value="" selected>All</option>
                <option value="store">Store</option>
                <option value="drop-ship">Drop-Ship</option>
              </select>
            </div>
            <input type="search" class="datatable-input datatable-type-filter hide" data-columns="[2]" />
            ${options.searchable ? `<div class="${options.classes.search}"><input class="${options.classes.input}" placeholder="${options.labels.placeholder}" type="search" title="${options.labels.searchTitle}"${dom.id ? ` aria-controls="${dom.id}"` : ""}></div>` : ""}
          </div>

          <div class="${options.classes.container}"${options.scrollY.length ? ` style="height: ${options.scrollY}; overflow-Y: auto;"` : ""}></div>

          <div class="${options.classes.bottom}">
            ${options.paging ? `<div class="${options.classes.info}"></div>` : ""}
            ${options.paging && options.perPageSelect ? `<div class="${options.classes.dropdown}"><label>${options.labels.perPage}</label><select class="${options.classes.selector}"></select></div>` : ""}
            <nav class="${options.classes.pagination}"></nav>
          </div>
        `
      });

      this.initializeTypeFilter();
      this.initializeRowClickHandler();
    } catch (error) {
      console.error('Error creating DataTable:', error);
    }
  }

  initializeTypeFilter() {
    const typeSelect = document.querySelector('.datatable-type-select');
    const typeFilter = document.querySelector('.datatable-top .datatable-type-filter');

    if (!typeSelect || !typeFilter) return;

    typeSelect.addEventListener('change', (e) => {
      typeFilter.value = e.target.value;
      typeFilter.dispatchEvent(new Event('input', { bubbles: true }));
    });

    typeSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }

  initializeRowClickHandler() {
    if (!this.dataTable) return;

    this.dataTable.on('datatable.page', (page) => {
      this.attachRowClickListeners();
    });

    this.dataTable.on('datatable.sort', (column, direction) => {
      localStorage.setItem('addressesDataTableSort', JSON.stringify({ column, direction }));
      this.attachRowClickListeners();
    });

    this.dataTable.on('datatable.search', (query, matched) => {
      this.attachRowClickListeners();
    });

    this.attachRowClickListeners();
  }

  attachRowClickListeners() {
    const rows = document.querySelectorAll('.table-addresses tbody tr');

    rows.forEach((row) => {
      row.addEventListener('click', (event) => {
        const rowIndex = parseInt(row.getAttribute('data-index'));

        if (
          !event.target.classList.contains('remove-address-button') &&
          !event.target.classList.contains('edit-address-form-button') &&
          !event.target.classList.contains('mark-favorite-address') &&
          !event.target.classList.contains('select-address-btn') &&
          !event.target.closest('.remove-address-button') &&
          !event.target.closest('.edit-address-form-button') &&
          !event.target.closest('.mark-favorite-address') &&
          !event.target.closest('.select-address-btn')
        ) {
          this.selectAddressFromRow(rowIndex);
        }
      });
    });
  }

  selectAddressFromRow(rowIndex) {
    const addressPlaceholder = document.querySelector('.address-placeholder');

    if (!addressPlaceholder) {
      return;
    }

    const el = document.querySelector(`.table-addresses tr[data-index="${rowIndex}"] .select-address-btn`);

    if (!el) {
      return;
    }

    const addressId = parseInt(el.getAttribute('data-shipping-address-id')) || '';
    const shippingAddressResponse = this.addresses.find((address) => parseInt(address.addressId) === addressId);

    if (!addressId || !shippingAddressResponse) {
      return;
    }

    addressPlaceholder.setAttribute('data-first-name', shippingAddressResponse.contactName || '');
    addressPlaceholder.setAttribute('data-contact-name', shippingAddressResponse.contactName || '');
    addressPlaceholder.setAttribute('data-company', shippingAddressResponse.company || '');
    addressPlaceholder.setAttribute('data-address-line1', shippingAddressResponse.address1 || '');
    addressPlaceholder.setAttribute('data-address-line2', shippingAddressResponse.address2 || '');
    addressPlaceholder.setAttribute('data-address-city', shippingAddressResponse.city || '');
    addressPlaceholder.setAttribute('data-address-country', shippingAddressResponse.countryCode || '');
    addressPlaceholder.setAttribute('data-address-province', shippingAddressResponse.provinceCode || '');
    addressPlaceholder.setAttribute('data-address-zip', shippingAddressResponse.zip || '');
    addressPlaceholder.setAttribute('data-phone', shippingAddressResponse.phone || '');
    addressPlaceholder.setAttribute('data-phone-extension', shippingAddressResponse.phoneExtension || '');

    let address = `
      <span class="cus-addr">${shippingAddressResponse.address1}</span>
      <span class="cus-addr">${shippingAddressResponse.address2 || ''}</span>
      <span class="cus-addr">${shippingAddressResponse.city || ''} ${shippingAddressResponse.province?.toUpperCase() || ''} ${shippingAddressResponse.zip || ''} ${shippingAddressResponse.country}</span>
    `;

    let phone = shippingAddressResponse?.phone || '';
    if (phone && shippingAddressResponse?.phoneExtension) {
      phone += ' x' + shippingAddressResponse.phoneExtension;
      address += `<span class="cus-addr">${phone}</span>`;
    }

    addressPlaceholder.innerHTML = `<span class="cus-addr">${shippingAddressResponse?.contactName || ''}</span>` + address;
    addressPlaceholder.setAttribute('data-address-id', addressId);
    addressPlaceholder.classList.remove('error');
    addressPlaceholder.classList.add('selected');

    localStorage.setItem('selectedShippingAddressId', addressId);

    document.querySelector('#orders .alert-messages')?.classList.remove('alert-show');
    document.querySelector('.change-shipping-address')?.classList.remove('hide');
    document.querySelector('.close-address-modal')?.click();

    this.alertMessages(
      '#orders .alert-messages',
      'success',
      'Ship To address updated.'
    );

    setTimeout(() => {
      document.querySelector('#orders .alert-messages')?.classList.add('fade-out');

      setTimeout(() => {
        document.querySelector('#orders .alert-messages')?.classList.remove('alert-show');
        document.querySelector('#orders .alert-messages')?.classList.remove('fade-out');
      }, 600);
    }, 3000);
  }

  autoSelectDefaultAddress() {
    // Only run on cart page
    if (window.location.pathname !== '/cart') {
      return;
    }

    const addressPlaceholder = document.querySelector('.address-placeholder');

    if (!addressPlaceholder || addressPlaceholder.classList.contains('selected')) {
      return;
    }

    // Check if there's a previously selected address in localStorage
    const savedAddressId = parseInt(localStorage.getItem('selectedShippingAddressId'));
    let defaultAddress = null;

    if (savedAddressId) {
      defaultAddress = this.addresses.find((address) => parseInt(address.addressId) === savedAddressId);
    }

    // If no saved address, use the default address
    if (!defaultAddress) {
      defaultAddress = this.addresses.find((address) => address.isDefault);
    }

    // If still no address, use the first address
    if (!defaultAddress && this.addresses.length > 0) {
      defaultAddress = this.addresses[0];
    }

    if (!defaultAddress) {
      return;
    }

    // Populate the address placeholder
    addressPlaceholder.setAttribute('data-first-name', defaultAddress.contactName || '');
    addressPlaceholder.setAttribute('data-contact-name', defaultAddress.contactName || '');
    addressPlaceholder.setAttribute('data-company', defaultAddress.company || '');
    addressPlaceholder.setAttribute('data-address-line1', defaultAddress.address1 || '');
    addressPlaceholder.setAttribute('data-address-line2', defaultAddress.address2 || '');
    addressPlaceholder.setAttribute('data-address-city', defaultAddress.city || '');
    addressPlaceholder.setAttribute('data-address-country', defaultAddress.countryCode || '');
    addressPlaceholder.setAttribute('data-address-province', defaultAddress.provinceCode || '');
    addressPlaceholder.setAttribute('data-address-zip', defaultAddress.zip || '');
    addressPlaceholder.setAttribute('data-phone', defaultAddress.phone || '');
    addressPlaceholder.setAttribute('data-phone-extension', defaultAddress.phoneExtension || '');
    addressPlaceholder.setAttribute('data-address-id', defaultAddress.addressId);

    let address = `
      <span class="cus-addr">${defaultAddress.address1}</span>
      <span class="cus-addr">${defaultAddress.address2 || ''}</span>
      <span class="cus-addr">${defaultAddress.city || ''} ${defaultAddress.province?.toUpperCase() || ''} ${defaultAddress.zip || ''} ${defaultAddress.country}</span>
    `;

    let phone = defaultAddress?.phone || '';
    if (phone && defaultAddress?.phoneExtension) {
      phone += ' x' + defaultAddress.phoneExtension;
      address += `<span class="cus-addr">${phone}</span>`;
    }

    addressPlaceholder.innerHTML = `<span class="cus-addr">${defaultAddress?.contactName || ''}</span>` + address;
    addressPlaceholder.classList.remove('error');
    addressPlaceholder.classList.add('selected');

    localStorage.setItem('selectedShippingAddressId', defaultAddress.addressId);

    document.querySelector('.change-shipping-address')?.classList.remove('hide');
  }

  formatAddressRowData(address, companyId) {
    const data = [];
    const tooltipTitle = 'Select this address';

    data.push(`<span>
        <span>
          <a href="javascript:void(0);"
            class="mark-favorite-address icon-star ${address.isFavorite ? 'icon-filled' : ''}"
            data-shipping-address-id="${address.addressId}">
            <span class="visually-hidden">${address.isFavorite ? 1 : 0}</span>
          </a>
        </span>
      </span>
    `);

    data.push(this.formatDate(address.createdAt));
    data.push(`<span title="${tooltipTitle}"><span>${address.type == 'drop-ship' ? 'Drop-ship' : 'Store'}</span></span>`);
    data.push(`<span title="${tooltipTitle}"><span>${address.company}<sup class="table-default-address" data-address-id="${address.addressId}"></sup></span></span>`);
    data.push(`<span title="${tooltipTitle}"><span>${address.contactName}</span></span>`);
    data.push(`
      <span title="${tooltipTitle}">
        <div class="address">
          <span class="cus-addr">${address.address1}</span>
          <span class="cus-addr">${address.address2 || ''}</span>
          <span class="cus-addr">${address?.city || ''} ${address.province?.toUpperCase() || ''} ${address?.zip || ''} ${address.country}</span>
        </div>
      </span>
    `);
    data.push(`<span title="${tooltipTitle}"><span>${address.phone} ${address.phoneExtension ? ('x' + address.phoneExtension) : ''}</span></span>`)

    return data;
  }

  updateDefaultAddressStyle() {
    const defaultAddress = this.addresses.find((address) => address.isDefault);
    const styleEl = document.getElementById('default-address-style');

    if (!styleEl) return;

    if (!defaultAddress) {
      styleEl.innerHTML = '';
      return;
    }

    styleEl.innerHTML = `
      .table-addresses sup.table-default-address[data-address-id="${this.escapeCss(defaultAddress.addressId)}"] {
        display: block;
      }
    `;
  }

  formatDate(value) {
    if (!value) return '';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleDateString('en-US');
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  escapeCss(value) {
    return String(value ?? '').replace(/"/g, '\\"');
  }
}
